import type { OperationAlert, Scenario } from "./types";

/**
 * 実 API が返すのは health と contacts のみのため、運用系はモックで形を先に固める。
 * 実装差し替え時は queries.ts の関数だけを SDK 呼び出しに置き換えればよい。
 */

/** 画面表示中に経過時間が進むよう、モジュール読み込み時刻を基準にする。 */
const ANCHOR = Date.now();

function minutesAgo(minutes: number): string {
  return new Date(ANCHOR - minutes * 60_000).toISOString();
}

/** スパークラインを毎回変えないための決定的な擬似乱数 */
function seededSeries(seed: number, length: number, base: number, spread: number): number[] {
  let state = seed;
  return Array.from({ length }, () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return Math.round(base + (state / 4294967296 - 0.5) * spread);
  });
}

export const scenariosFixture: Scenario[] = [
  {
    id: "scn_abandoned_checkout",
    name: "カゴ落ち回収",
    category: "顧客起点",
    trigger: "checkouts/update",
    state: "paused",
    shopDomain: "aurora-living.myshopify.com",
    owner: "田中 慧",
    deliveries24h: 0,
    openRate: 0,
    clickRate: 0,
    conversions24h: 0,
    revenue24h: 0,
    errorRate: 100,
    lastRunAt: minutesAgo(134),
    trend: seededSeries(11, 14, 820, 260)
  },
  {
    id: "scn_welcome_series",
    name: "ウェルカムシリーズ",
    category: "顧客起点",
    trigger: "customers/create",
    state: "running",
    shopDomain: "aurora-living.myshopify.com",
    owner: "佐藤 みなみ",
    deliveries24h: 1284,
    openRate: 48.2,
    clickRate: 12.4,
    conversions24h: 63,
    revenue24h: 486_200,
    errorRate: 0.2,
    lastRunAt: minutesAgo(3),
    trend: seededSeries(23, 14, 1240, 220)
  },
  {
    id: "scn_post_purchase",
    name: "購入後フォロー",
    category: "顧客起点",
    trigger: "orders/create",
    state: "running",
    shopDomain: "aurora-living.myshopify.com",
    owner: "佐藤 みなみ",
    deliveries24h: 942,
    openRate: 52.7,
    clickRate: 14.1,
    conversions24h: 71,
    revenue24h: 612_400,
    errorRate: 0.1,
    lastRunAt: minutesAgo(7),
    trend: seededSeries(37, 14, 930, 180)
  },
  {
    id: "scn_winback",
    name: "休眠掘り起こし",
    category: "顧客起点",
    trigger: "schedule/daily",
    state: "degraded",
    shopDomain: "kaede-craft.myshopify.com",
    owner: "未割当",
    deliveries24h: 318,
    openRate: 21.4,
    clickRate: 3.2,
    conversions24h: 6,
    revenue24h: 38_900,
    errorRate: 6.8,
    lastRunAt: minutesAgo(41),
    trend: seededSeries(53, 14, 420, 300)
  },
  {
    id: "scn_vip",
    name: "VIP育成",
    category: "顧客起点",
    trigger: "orders/paid",
    state: "running",
    shopDomain: "aurora-living.myshopify.com",
    owner: "田中 慧",
    deliveries24h: 164,
    openRate: 63.9,
    clickRate: 24.8,
    conversions24h: 29,
    revenue24h: 894_100,
    errorRate: 0,
    lastRunAt: minutesAgo(12),
    trend: seededSeries(71, 14, 150, 60)
  },
  {
    id: "scn_back_in_stock",
    name: "再入荷通知",
    category: "商品起点",
    trigger: "inventory_levels/update",
    state: "degraded",
    shopDomain: "kaede-craft.myshopify.com",
    owner: "鈴木 涼",
    deliveries24h: 226,
    openRate: 58.1,
    clickRate: 31.2,
    conversions24h: 34,
    revenue24h: 271_800,
    errorRate: 4.1,
    lastRunAt: minutesAgo(28),
    trend: seededSeries(97, 14, 240, 140)
  },
  {
    id: "scn_dead_stock",
    name: "滞留在庫の消化",
    category: "商品起点",
    trigger: "schedule/weekly",
    state: "running",
    shopDomain: "kaede-craft.myshopify.com",
    owner: "鈴木 涼",
    deliveries24h: 512,
    openRate: 33.6,
    clickRate: 7.8,
    conversions24h: 18,
    revenue24h: 128_400,
    errorRate: 0.4,
    lastRunAt: minutesAgo(96),
    trend: seededSeries(113, 14, 480, 210)
  },
  {
    id: "scn_stockout_guard",
    name: "欠品予防アラート",
    category: "商品起点",
    trigger: "inventory_levels/update",
    state: "running",
    shopDomain: "aurora-living.myshopify.com",
    owner: "鈴木 涼",
    deliveries24h: 48,
    openRate: 71.2,
    clickRate: 0,
    conversions24h: 0,
    revenue24h: 0,
    errorRate: 0,
    lastRunAt: minutesAgo(18),
    trend: seededSeries(131, 14, 52, 30)
  },
  {
    id: "scn_launch",
    name: "新商品ローンチ",
    category: "商品起点",
    trigger: "products/create",
    state: "draft",
    shopDomain: "aurora-living.myshopify.com",
    owner: "未割当",
    deliveries24h: 0,
    openRate: 0,
    clickRate: 0,
    conversions24h: 0,
    revenue24h: 0,
    errorRate: 0,
    lastRunAt: minutesAgo(2880),
    trend: seededSeries(151, 14, 0, 0)
  },
  {
    id: "scn_sale_alert",
    name: "セール連動値下げ通知",
    category: "商品起点",
    trigger: "products/update",
    state: "running",
    shopDomain: "kaede-craft.myshopify.com",
    owner: "佐藤 みなみ",
    deliveries24h: 736,
    openRate: 44.9,
    clickRate: 18.6,
    conversions24h: 52,
    revenue24h: 349_700,
    errorRate: 0.6,
    lastRunAt: minutesAgo(22),
    trend: seededSeries(173, 14, 700, 240)
  },
  {
    id: "scn_structured_data",
    name: "構造化データ自動整備",
    category: "AIO",
    trigger: "products/update",
    state: "degraded",
    shopDomain: "aurora-living.myshopify.com",
    owner: "山本 大地",
    deliveries24h: 1042,
    openRate: 0,
    clickRate: 0,
    conversions24h: 0,
    revenue24h: 0,
    errorRate: 8.3,
    lastRunAt: minutesAgo(9),
    trend: seededSeries(191, 14, 980, 320)
  },
  {
    id: "scn_llms_txt",
    name: "llms.txt 自動運用",
    category: "AIO",
    trigger: "schedule/daily",
    state: "running",
    shopDomain: "aurora-living.myshopify.com",
    owner: "山本 大地",
    deliveries24h: 1,
    openRate: 0,
    clickRate: 0,
    conversions24h: 0,
    revenue24h: 0,
    errorRate: 0,
    lastRunAt: minutesAgo(312),
    trend: seededSeries(211, 14, 1, 0)
  },
  {
    id: "scn_ai_traffic",
    name: "AI流入の計測",
    category: "AIO",
    trigger: "schedule/hourly",
    state: "running",
    shopDomain: "aurora-living.myshopify.com",
    owner: "山本 大地",
    deliveries24h: 24,
    openRate: 0,
    clickRate: 0,
    conversions24h: 0,
    revenue24h: 0,
    errorRate: 0,
    lastRunAt: minutesAgo(34),
    trend: seededSeries(233, 14, 24, 4)
  },
  {
    id: "scn_delivery_tuning",
    name: "配信の自動改善",
    category: "AIOps",
    trigger: "schedule/daily",
    state: "running",
    shopDomain: "aurora-living.myshopify.com",
    owner: "田中 慧",
    deliveries24h: 6,
    openRate: 0,
    clickRate: 0,
    conversions24h: 0,
    revenue24h: 0,
    errorRate: 0,
    lastRunAt: minutesAgo(58),
    trend: seededSeries(251, 14, 6, 2)
  },
  {
    id: "scn_conversation_watch",
    name: "会話のAI監視",
    category: "AIOps",
    trigger: "schedule/5min",
    state: "running",
    shopDomain: "kaede-craft.myshopify.com",
    owner: "佐藤 みなみ",
    deliveries24h: 288,
    openRate: 0,
    clickRate: 0,
    conversions24h: 0,
    revenue24h: 0,
    errorRate: 1.2,
    lastRunAt: minutesAgo(1),
    trend: seededSeries(271, 14, 288, 12)
  }
];

export const alertsFixture: OperationAlert[] = [
  {
    id: "alt_flow_paused",
    severity: "critical",
    title: "カゴ落ち回収フローが停止",
    source: "カゴ落ち回収",
    shopDomain: "aurora-living.myshopify.com",
    detail:
      "配信プロバイダが 3 回連続で 5xx を返したため自動停止しました。復旧後に手動で再開が必要です。",
    startedAt: minutesAgo(134),
    acknowledged: false,
    assignee: null,
    action: { label: "シナリオを開く", to: "/scenarios" }
  },
  {
    id: "alt_bounce_rate",
    severity: "critical",
    title: "バウンス率がしきい値を超過",
    source: "配信基盤",
    shopDomain: "kaede-craft.myshopify.com",
    detail: "直近 1 時間のハードバウンス率が 5.2%（上限 3.0%）。送信ドメインの評価に影響します。",
    startedAt: minutesAgo(47),
    acknowledged: true,
    assignee: "田中 慧",
    action: { label: "対象の顧客を確認", to: "/contacts" }
  },
  {
    id: "alt_inventory_lag",
    severity: "warning",
    title: "在庫同期が遅延",
    source: "再入荷通知",
    shopDomain: "kaede-craft.myshopify.com",
    detail: "inventory_levels/update の処理が平均 8 分遅延しています。再入荷通知の配信も遅れます。",
    startedAt: minutesAgo(41),
    acknowledged: false,
    assignee: "鈴木 涼",
    action: { label: "シナリオを開く", to: "/scenarios" }
  },
  {
    id: "alt_webhook_retry",
    severity: "warning",
    title: "Webhook が再試行中",
    source: "Worker",
    shopDomain: "aurora-living.myshopify.com",
    detail: "orders/create の 12 件が再試行キューに滞留しています。3 回目で失敗扱いになります。",
    startedAt: minutesAgo(12),
    acknowledged: false,
    assignee: null,
    action: { label: "Worker の状態を見る", to: "/" }
  },
  {
    id: "alt_schema_invalid",
    severity: "warning",
    title: "構造化データの検証エラー",
    source: "構造化データ自動整備",
    shopDomain: "aurora-living.myshopify.com",
    detail: "86 商品で Product スキーマの必須項目（offers.price）が欠落しています。",
    startedAt: minutesAgo(96),
    acknowledged: true,
    assignee: "山本 大地",
    action: { label: "シナリオを開く", to: "/scenarios" }
  },
  {
    id: "alt_rate_limit",
    severity: "warning",
    title: "Shopify API のレート制限に接近",
    source: "Worker",
    shopDomain: "kaede-craft.myshopify.com",
    detail: "直近 5 分の使用率が 87%。この推移だと約 20 分でスロットリングされます。",
    startedAt: minutesAgo(6),
    acknowledged: false,
    assignee: null,
    action: { label: "Worker の状態を見る", to: "/" }
  },
  {
    id: "alt_winback_low",
    severity: "info",
    title: "休眠掘り起こしの開封率が基準を下回る",
    source: "休眠掘り起こし",
    shopDomain: "kaede-craft.myshopify.com",
    detail: "開封率 21.4%（基準 25.0%）。件名の自動改善候補が 3 件生成されています。",
    startedAt: minutesAgo(220),
    acknowledged: true,
    assignee: "佐藤 みなみ",
    action: { label: "分析を開く", to: "/analytics" }
  },
  {
    id: "alt_ai_citation",
    severity: "info",
    title: "AI検索での引用が減少",
    source: "引用モニタリング",
    shopDomain: "aurora-living.myshopify.com",
    detail: "主要 12 クエリのうち 4 件で自社ページの引用が消失しました。",
    startedAt: minutesAgo(410),
    acknowledged: false,
    assignee: null,
    action: { label: "分析を開く", to: "/analytics" }
  },
  {
    id: "alt_new_shop",
    severity: "info",
    title: "新しいストアが接続されました",
    source: "接続",
    shopDomain: "kaede-craft.myshopify.com",
    detail: "初回の顧客同期が完了しました。シナリオの割当が未設定です。",
    startedAt: minutesAgo(690),
    acknowledged: true,
    assignee: "田中 慧",
    action: { label: "シナリオを開く", to: "/scenarios" }
  }
];
